import React, { useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { cloneDeep, get, isEmpty } from 'lodash';
import { Grid } from '@material-ui/core';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import ImportImagineDocumentButton from 'containers/CourtManagerLegalProceedings/ImagineDocuments/ImportButton';
import { resolveSagisClaim } from 'helpers/utils';
import { Listings } from 'containers/AppContainer/typings';
import { JudgementAddFormContainer } from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/styled-components';
import ExpertiseAddForms from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/ExpertiseAddForms';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import {
  ExpertiseState,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { EMPTY_EXPERTISE } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/constants';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import {
  isJudgementValid,
  judgementAddValidator,
} from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/validator';
import { formattedJudgementAddPronouncing } from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/utils';
import JudgementAddFormFields from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/JudgementAddFormFields';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import FormValidationButtons from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/FormValidationButtons';
import { DOCUMENT_TYPES } from 'constants/global';
import { arrayTranslate } from 'containers/services/translate';
import { displayErrorText } from 'containers/services/validateForm';
import { VictimType } from 'typings/globalTypes';

export interface JudgementAddPronouncingFormProps {
  intl: IntlShape;
  listings: Listings;
  victims: VictimType[];
  vehicules: Vehicule[];
  judgementAddPronouncing: JudgementADDDocumentType;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  handleSetJudgementAddPronouncing: Function;
  index: number;
  saveForm: Function;
  cancelJudgement: Function;
  details: any;
  indexMap?: number;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  isBasicManagement?: boolean;
  imagineDocuments: any;
  policyInfo: any;
  claimId: number;
  requestGetDocByDocumentumId: Function;
}

const JudgementAddPronouncingForm: React.FC<JudgementAddPronouncingFormProps> = (
  props,
): JSX.Element => {
  const [expertises, setExpertises] = useState<ExpertiseState[]>([
    cloneDeep(EMPTY_EXPERTISE),
  ]);

  const [expertiseDate, setExpertiseDate] = useState<any>(null);

  const [disabledSaveButton, setDisabledSaveButton] = useState<boolean>(true);
  const [judgementAddValidation, setJudgementAddValidation] = useState<any>({});
  const [messageJudgementError, setMessageJudgementError] = useState<any>({});

  React.useEffect(() => {
    const judgementAdd = formattedJudgementAddPronouncing(
      expertises,
      props.victims,
      props.vehicules,
      props.judgementAddPronouncing,
    );

    const validateJudgementAdd = judgementAddValidator(judgementAdd);
    setJudgementAddValidation(validateJudgementAdd);
    const translateArrayErrorMessages = arrayTranslate(
      props.intl.formatMessage,
    );
    const { message } = displayErrorText(
      validateJudgementAdd,
      translateArrayErrorMessages(validateJudgementAdd, true),
    );
    setMessageJudgementError(message);
    setDisabledSaveButton(
      !isJudgementValid(judgementAdd, get(props.details, 'hearingDate', '')) ||
        get(props.judgementAddPronouncing, 'judgementDate') === 'Invalid date',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expertises,
    props.judgementAddPronouncing,
    props.vehicules,
    props.victims,
  ]);

  const handleSaveJudgementAddDocument = () => {
    const judgementAdd = formattedJudgementAddPronouncing(
      expertises,
      props.victims,
      props.vehicules,
      props.judgementAddPronouncing,
    );

    props.saveForm(judgementAdd);

    setExpertises([]);
    props.handleSetJudgementAddPronouncing({});
  };

  const handleCancelJudgementAdd = () => {
    props.cancelJudgement();
  };
  const getIndex = () => {
    return props.indexMap !== undefined ? props.indexMap : props.index;
  };

  return (
    <JudgementAddFormContainer>
      <JudgementAddFormFields
        index={props.index}
        handleSetJudgementAddPronouncing={
          props.handleSetJudgementAddPronouncing
        }
        judgementAddPronouncing={props.judgementAddPronouncing}
        listings={props.listings}
        details={props.details}
        setExpertiseDate={setExpertiseDate}
        expertiseDate={expertiseDate}
        judgementAddValidation={judgementAddValidation}
        messageJudgementError={messageJudgementError}
        hearingDate={get(props.details, 'hearingDate', '')}
      />
      <section className="expertise-form">
        <ExpertiseAddForms
          judgementAddPronouncing={props.judgementAddPronouncing}
          listings={props.listings}
          victims={props.victims}
          vehicules={props.vehicules}
          expertises={expertises}
          setExpertises={setExpertises}
          setExpertiseDate={setExpertiseDate}
          expertiseDate={expertiseDate}
          sagisVictimInfo={props.sagisVictimInfo}
          sagisOpposingParties={props.sagisOpposingParties}
          isBasicManagement={props.isBasicManagement}
        />
      </section>
      <Grid container direction="row" alignItems={'center'} spacing={2}>
        <Grid item xs={4}>
          <UploadDocumentBloc
            document={props.judgementAddPronouncing}
            setDocument={props.handleSetJudgementAddPronouncing}
            index={getIndex()}
            resetUploadedDocument={props.resetUploadedDocument}
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            docType={DOCUMENT_TYPES.JUDGEMENT_ADD}
          />
        </Grid>
        <Grid item xs={4} style={{ marginTop: '45px' }}>
          {!isEmpty(props.imagineDocuments) &&
            featureService.hasFeature(FEATURES.TRAITEMENT_IMAGINE_DOC) && (
              <ImportImagineDocumentButton
                listings={props.listings}
                managerNotifcationsInboxes={props.imagineDocuments}
                claimId={props.claimId}
                imagineDocuments={props.imagineDocuments}
                claimSagisInfo={resolveSagisClaim(props.policyInfo)}
                uploadDocument={props.uploadDocument}
                uploadedDocument={props.uploadedDocument}
                setUploadedDocs={props.handleSetJudgementAddPronouncing}
                documentName={DOCUMENT_TYPES.JUDGEMENT_ADD}
                documentType={DOCUMENT_TYPES.JUDGEMENT_ADD}
                documentId={''}
                document={props.judgementAddPronouncing}
                setDocumentId={() => {}}
                requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
              />
            )}
        </Grid>
      </Grid>
      <FormValidationButtons
        saveForm={handleSaveJudgementAddDocument}
        cancelForm={handleCancelJudgementAdd}
        disabled={disabledSaveButton}
      />
    </JudgementAddFormContainer>
  );
};

export default injectIntl(JudgementAddPronouncingForm);
